import { useCallback, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TimelineFeedItem } from '@/components/timeline/TimelineFeedItem';
import { Bell, Send } from 'lucide-react';
import type { ProfileContentProps } from './types';

type NotificationsTabContentProps = {
  patientId: number;
  pushNotifications: ProfileContentProps['pushNotifications'];
};

export default function NotificationsTabContent({
  patientId,
  pushNotifications,
}: NotificationsTabContentProps) {
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(
    pushNotifications.templates[0]?.key ?? '',
  );
  const [pushStatus, setPushStatus] = useState<{
    variant: 'success' | 'error';
    message: string;
  } | null>(null);
  const [pushSending, setPushSending] = useState(false);
  const hasTemplates = pushNotifications.templates.length > 0;
  const manualPushHistory = pushNotifications.history ?? [];
  const hasActiveDevice = pushNotifications.hasActiveDevice;

  const selectedTemplate = useMemo(
    () => pushNotifications.templates.find((template) => template.key === selectedTemplateKey),
    [pushNotifications.templates, selectedTemplateKey],
  );

  const handleSendManualPush = useCallback(async () => {
    if (!selectedTemplate) {
      setPushStatus({ variant: 'error', message: 'Seleziona un modello valido.' });
      return;
    }
    if (!pushNotifications.hasActiveDevice) {
      setPushStatus({ variant: 'error', message: 'Nessun dispositivo collegato.' });
      return;
    }

    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
    setPushSending(true);
    setPushStatus(null);

    try {
      const res = await fetch(`/therapists/patients/${patientId}/manual_push_notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf || '',
        },
        credentials: 'include',
        body: JSON.stringify({ manual_push: { template_key: selectedTemplate.key } }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPushStatus({
          variant: 'error',
          message: payload.message || 'Invio non riuscito. Riprova.',
        });
        return;
      }
      setPushStatus({
        variant: 'success',
        message: 'Notifica inviata. Il paziente la riceverà a breve.',
      });
    } catch {
      setPushStatus({ variant: 'error', message: 'Errore di rete. Riprova.' });
    } finally {
      setPushSending(false);
    }
  }, [patientId, pushNotifications.hasActiveDevice, selectedTemplate]);

  return (
    <>
      {!hasActiveDevice ? (
        <EmptyState
          title="Nessun dispositivo registrato"
          description="Il cliente deve prima accedere all'app e accettare le notifiche push."
          icon={<Bell className="h-6 w-6" aria-hidden />}
          variant="contrast"
          className="py-10"
        />
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>Invia notifica push</CardTitle>
                <CardDescription>
                  Seleziona un modello e invia un promemoria mirato al paziente.
                </CardDescription>
              </div>
              <Badge variant={hasActiveDevice ? 'outline' : 'secondary'}>
                {hasActiveDevice ? 'Dispositivo attivo' : 'Nessun dispositivo'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
              <div className="space-y-2">
                <Label>Modello</Label>
                <Select
                  value={selectedTemplateKey}
                  onValueChange={(value) => {
                    setSelectedTemplateKey(value);
                    setPushStatus(null);
                  }}
                  disabled={!hasTemplates}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona un modello" />
                  </SelectTrigger>
                  <SelectContent>
                    {pushNotifications.templates.map((template) => (
                      <SelectItem key={template.key} value={template.key}>
                        {template.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-3 justify-end">
                <Button
                  onClick={handleSendManualPush}
                  disabled={!selectedTemplate || !hasActiveDevice || pushSending}
                  className="gap-2"
                >
                  <Send className="h-4 w-4" />
                  {pushSending ? 'Invio in corso…' : 'Invia notifica'}
                </Button>
                {pushStatus && (
                  <p
                    className={`text-sm ${
                      pushStatus.variant === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {pushStatus.message}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Anteprima</Label>
              <div className="rounded-lg border border-border/70 bg-muted/30 p-4 space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {selectedTemplate?.title ||
                    (hasTemplates ? 'Seleziona un modello' : 'Nessun modello disponibile')}
                </p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {selectedTemplate?.body ||
                    (hasTemplates
                      ? 'Scegli un modello per vedere il testo.'
                      : "Non ci sono modelli disponibili per l'invio manuale.")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {manualPushHistory.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Ultime notifiche inviate</CardTitle>
            <CardDescription>Storico delle ultime 10 notifiche push.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-3">
              {manualPushHistory.map((item) => {
                const statusLabel =
                  item.status === 'sent' ? 'Inviata' : item.status === 'failed' ? 'Fallita' : 'In coda';
                return (
                  <TimelineFeedItem
                    key={item.id}
                    tone="journal"
                    timeLabel={new Date(item.sentAt).toLocaleString('it-IT')}
                    label={`${item.title} · ${statusLabel}`}
                    summary={item.body || '—'}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}
